<script>
import Layout from '@layouts/main'
import { mapGetters } from 'vuex'
import { postHotelRoomScheduleStore, getHotelRoomClosedDates } from '@src/router/views/hotel/api'

export default {
  data() {
    // 獲取今天
    const now = new Date()
    now.setHours(0, 0, 0, 0) // 清除時間，只留日期比較

    // 獲取 90 天後
    const limit = new Date(now)
    limit.setDate(now.getDate() + 90)
    return {
      room: {
        name: '',
      },
      form: {
        roomIds: '',
        closedDates: [], // 現在存放格式為 ["2025-02-10", "2025-02-11"]
        startDate: now, // 限制起點
        endDate: limit, // 限制終點 (90天後)
      },
      currentMonth: new Date(now.getFullYear(), now.getMonth(), 1), // 初始化為當前月份
      weekdays: ['一', '二', '三', '四', '五', '六', '日'],
    }
  },
  components: {
    Layout,
  },
  computed: {
    calendarDays() {
      const year = this.currentMonth.getFullYear()
      const month = this.currentMonth.getMonth()
      const firstDay = new Date(year, month, 1)

      let startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
      const calendarStart = new Date(firstDay)
      calendarStart.setDate(firstDay.getDate() - startOffset)

      const days = []
      const curr = new Date(calendarStart)

      for (let i = 0; i < 42; i++) {
        const dateCopy = new Date(curr)
        const dateString = this.formatDate(dateCopy)
        const isDisabled = dateCopy < this.form.startDate || dateCopy > this.form.endDate

        days.push({
          date: dateCopy,
          dateString: dateString,
          isCurrentMonth: dateCopy.getMonth() === month,
          // 邏輯轉換：如果在 closedDates 裡面，代表它是「關閉」狀態
          isClosed: this.form.closedDates.includes(dateString),
          isDisabled: isDisabled,
        })
        curr.setDate(curr.getDate() + 1)
      }
      return days
    },
    currentMonthText() {
      return `${this.currentMonth.getFullYear()}年${this.currentMonth.getMonth() + 1}月`
    },
    isMinMonth() {
      const prevMonthLastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 0)
      return prevMonthLastDay < this.form.startDate
    },
    isMaxMonth() {
      const nextMonthFirstDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1)
      return nextMonthFirstDay > this.form.endDate
    },
    ...mapGetters('hotel/setting', ['productId', 'productName']),
  },
  async created() {
    this.form.roomIds = this.$route.params.id
    await this.fetchHotelRoomClosedDates()
  },
  mounted() {
    if (!this.productId && !this.productName) {
      this.$confirm('請返回房型列表重新進入', '載入錯誤', {
        confirmButtonText: '確定',
        type: 'error',
        showCancelButton: false,
        showClose: false,
      }).then(() => {
        this.$router.back()
        this.$message({
          type: 'success',
          message: '已返回房型列表頁面',
        })
      })
    }
  },
  methods: {
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    previousMonth() {
      const prevMonthLastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 0)
      if (prevMonthLastDay < this.form.startDate) return
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1)
    },
    nextMonth() {
      const nextMonthFirstDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1)
      if (nextMonthFirstDay > this.form.endDate) return
      this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1)
    },
    toggleDaySelection(day) {
      if (day.isDisabled) return

      const dateStr = day.dateString
      const index = this.form.closedDates.indexOf(dateStr)

      if (index > -1) {
        // 如果已經在裡面，移除它（代表重新開放）
        this.form.closedDates.splice(index, 1)
      } else {
        // 如果不在裡面，加入它（代表關閉該日期）
        this.form.closedDates.push(dateStr)
      }
    },
    clearSelectedDates() {
      this.form.closedDates = []
    },
    async onClickSubmit() {
      const loadingInstance = this.$loading({ lock: true, text: '儲存中...' })
      try {
        await this.fetchHotelRoomScheduleStore()
        this.$message.success('排班時段儲存成功')
        this.$router.push('/hotel?tab1=1')
      } catch (error) {
        console.log('Submit Error:', error)
        this.$message.error('排班時段儲存失敗')
      } finally {
        loadingInstance.close()
      }
    },

    async fetchHotelRoomScheduleStore() {
      const payload = {
        roomIds: [this.form.roomIds],
        closedDates: this.form.closedDates || [],
        startDate: this.formatDate(this.form.startDate),
        endDate: this.formatDate(this.form.endDate),
      }
      return postHotelRoomScheduleStore(payload)
    },

    async fetchHotelRoomClosedDates() {
      const loadingInstance = this.$loading({ lock: true, text: '讀取中...' })
      try {
        const payload = {
          startDate: this.formatDate(this.form.startDate),
          endDate: this.formatDate(this.form.endDate),
        }
        const res = await getHotelRoomClosedDates(this.productId, this.form.roomIds, payload)
        this.form.closedDates = res.items.closedDates || []
        this.room.name = res.items.title || ''
      } catch (error) {
        console.log('取得不開放日期失敗', error)
        this.$message.error('取得不開放日期失敗')
      } finally {
        loadingInstance.close()
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div class="scheduling">
      <h1>{{ room.name }}｜排班時段</h1>
      <el-form class="scheduling_content">
        <div class="button-row">
          <div class="calendar-instruction">
            <p class="instruction-text"> 系統自動開放今日起算 <span class="color-primary">90天內</span> 的時段。您可以點擊下方日期，將特定日期切換為「不開放」 </p>

            <div class="legend-container">
              <div class="legend-item">
                <span class="dot orange">●</span>
                <span class="label primary">橘色：開放預約</span>
              </div>

              <div class="legend-item">
                <span class="dot gray">●</span>
                <span class="label secondary">灰色：不開放</span>
              </div>
            </div>
          </div>

          <el-button round plain @click="clearSelectedDates">取消當前選取日期</el-button>
        </div>
        <div class="calendar-section">
          <div class="calendar-header">
            <el-button type="text" icon="el-icon-arrow-left" @click="previousMonth" :class="['month-nav-btn', { 'is-disabled': isMinMonth }]" />
            <span class="current-month">{{ currentMonthText }}</span>
            <el-button type="text" icon="el-icon-arrow-right" @click="nextMonth" :class="['month-nav-btn', { 'is-disabled': isMaxMonth }]" />
          </div>

          <div class="calendar-grid">
            <div class="calendar-weekdays">
              <div class="weekday" v-for="day in weekdays" :key="day">{{ day }}</div>
            </div>

            <div class="calendar-days">
              <div v-for="(day, index) in calendarDays" :key="index" class="calendar-day">
                <div
                  class="calendar-day-number"
                  :class="{
                    'other-month': !day.isCurrentMonth,
                    'is-disabled': day.isDisabled /* 90天外的灰色 */,
                    'in-range': !day.isDisabled /* 90天內的亮色預設 */,
                    'is-closed': day.isClosed /* 被點選後的暗色 */,
                    'current-month': day.isCurrentMonth,
                  }"
                  @click="toggleDaySelection(day)"
                >
                  {{ day.date.getDate() }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-form>
    </div>

    <div class="footer">
      <el-button class="is-round" @click="$router.back()">取消編輯</el-button>
      <el-button class="is-round" type="primary" @click="onClickSubmit">
        <span>儲存編輯</span>
      </el-button>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>

.footer {
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.14);
  display: flex;
  justify-content: center;
  position: fixed;
  bottom: 0;
  left: $size-sidebar-width;
  z-index: $layer-page-footer-z-index;
  width: calc(100% - #{$size-sidebar-width});
  padding: 20px 0;
  padding-left: 5%;
  padding-right: 5%;
  text-align: center;
  background-color: white;

  > * {
    flex: 1 0 0;
  }
}
</style>

<style lang="scss" scoped>
.scheduling {
  h1 {
    padding: 20px 16px 0;
    font-size: 20px;
    color: #121931;
    margin-bottom: 20px;
  }

  .scheduling_content {
    padding: 0px 16px 60px;
    margin-bottom: 100px;
    background-color: #ffffff;
    .reservation-settings,
    .repeat-settings {
      margin-bottom: 32px;

      .settings-row {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .setting-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f9f9fb;
          border-radius: 8px;

          .setting-label {
            flex: 1;
            font-weight: 500;
            color: #121931;
          }

          .setting-value {
            color: #ea6a2a;
            font-weight: 500;
            min-width: 20px;
          }

          .repeat-select {
            width: 200px;
          }

          .selected-days {
            color: #ea6a2a;
            font-weight: 500;
          }
        }
      }
    }

    .calendar-section {
      margin-top: 24px;
      border: 1px solid #f4f4f4;
      border-radius: 8px;

      .calendar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 30px;
        padding-left: 30px;
        padding-right: 30px;

        .current-month {
          font-size: 18px;
          font-weight: 600;
          color: #121931;
          min-width: 120px;
          text-align: center;
        }

        .month-nav-btn {
          color: #121931;
          font-size: 30px;

          &:hover {
            color: #ea6a2a;
          }
          &.is-disabled {
            color: #d1d5db;
            cursor: not-allowed;
            &:hover {
              color: #d1d5db;
            }
          }
        }
      }

      .calendar-grid {
        background: white;
        border-radius: 8px;

        .calendar-weekdays {
          display: grid;
          grid-template-columns: repeat(7, 1fr);

          .weekday {
            padding: 12px;
            text-align: center;
            font-weight: 600;
            color: #666;
          }
        }

        .calendar-days {
          display: grid;
          grid-template-columns: repeat(7, 1fr);

          .calendar-day {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 2px;
            margin-bottom: 2px;

            .calendar-day-number {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 44px;
              height: 44px;
              font-size: 16px;
              color: #121931;
              border-radius: 4px;
              transition: all 0.2s ease;
              cursor: pointer;

              // 1. 預設 90 天內的亮色 (開放預約)
              &.in-range {
                background-color: #fff4ee; // 極淺橘色背景，代表開放
                color: #ea6a2a; // 橘色文字
                border: 1px solid #ea6a2a;
                font-weight: 600;

                &:hover {
                  background-color: #ea6a2a;
                  color: #ffffff;
                }
              }

              // 2. 被點擊後（存入 closedDates）變黯淡
              &.is-closed {
                background-color: #f3f4f6 !important; // 灰色背景
                color: #9ca3af !important; // 灰色文字
                text-decoration: line-through; // 加上刪除線，更直觀表示「關閉」
                border: 1px solid #e5e7eb !important;

                &:hover {
                  background-color: #e5e7eb !important;
                }
              }

              // 3. 90 天之外的完全禁用
              &.is-disabled {
                background-color: transparent !important;
                color: #d1d5db !important;
                cursor: not-allowed !important;
                border: none !important;
              }
            }
          }
        }
      }
    }
  }
  .calendar-instruction {
    padding: 0 4px;

    .instruction-text {
      font-size: 16px;
      color: #121931;
      line-height: 1.6;
      margin-bottom: 12px;
      font-weight: 500;
    }

    .legend-container {
      display: flex;
      gap: 20px;
      align-items: center;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;

        .dot {
          font-size: 18px;
          line-height: 1;

          &.orange {
            color: #ea6a2a;
          }
          &.gray {
            color: #d1d5db;
          }
        }

        .label {
          &.primary {
            color: #121931;
            font-weight: 500;
          }
          &.secondary {
            color: #999;
          }
        }
      }
    }
  }
  .color-primary {
    color: #ea6a2a;
  }
  .button-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
